import { Injectable } from "@angular/core";
import {
  PublicClientApplication,
  Configuration,
  AccountInfo,
  AuthenticationResult,
} from "@azure/msal-browser";
import { firstValueFrom } from "rxjs";
import { StateService } from "../../services/state.service";
import { HttpService } from "../../services/http.service";

@Injectable({ providedIn: "root" })
export class ChatbotMsalService {
  private msalInstance!: PublicClientApplication;
  private initPromise: Promise<void> | null = null;
  private redirectPromise: Promise<AuthenticationResult | null> | null = null;
  private scopes: string[] = [];

  constructor(
    private stateService: StateService,
    private httpService: HttpService,
  ) {}

  private buildMsalConfig(config: any): Configuration {
    this.scopes = [`api://${config.ic_client_id}/insights_copilot`];
    return {
      auth: {
        clientId: config.client_id,
        authority: `https://login.microsoftonline.com/${config.tenant_id}`,
        redirectUri: window.location.origin + "/",
      },
      cache: {
        cacheLocation: "sessionStorage",
      },
    };
  }

  async initialize(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = (async () => {
        if (!this.msalInstance) {
          let config = this.stateService.copilotConfig;
          if (!config) {
            const configUrl = this.stateService.apiList.copilot.config;
            config = await firstValueFrom(
              this.httpService.getMethod(configUrl),
            );
            if (config) {
              this.stateService.setCopilotConfig(config);
            }
          }
          if (!config) {
            this.initPromise = null;
            throw new Error(
              "Copilot config not available. Cannot initialize MSAL.",
            );
          }
          this.msalInstance = new PublicClientApplication(
            this.buildMsalConfig(config),
          );
        }
        await this.msalInstance.initialize();
      })();
    }
    return this.initPromise;
  }

  async handleRedirect(): Promise<AuthenticationResult | null> {
    if (!this.redirectPromise) {
      this.redirectPromise = this.msalInstance.handleRedirectPromise();
    }
    return this.redirectPromise;
  }

  getAccount(): AccountInfo | null {
    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }

  async acquireToken(): Promise<string> {
    await this.initialize();
    const account = this.getAccount();
    if (!account) {
      throw new Error("No MSAL account found. User must authenticate first.");
    }

    const result = await this.msalInstance.acquireTokenSilent({
      scopes: this.scopes,
      account,
    });
    return result.accessToken;
  }

  async loginRedirect(): Promise<void> {
    // Save the current URL so we can restore it after the redirect
    sessionStorage.setItem("chatbot_redirect_url", window.location.href);
    await this.msalInstance.loginRedirect({
      scopes: this.scopes,
    });
  }
}
